import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  useBulkUpdateEmployerJobs,
  useEmployerJobs,
  useUpdateEmployerJobStatus,
} from '../../hooks/useEmployerJobs';
import type { EmployerJobStatus } from '../../api/employerJobs';

const jobStates = ['all', 'draft', 'pending_review', 'approved', 'rejected', 'active'] as const;

export function JobManagement() {
  const [activeTab, setActiveTab] = useState<(typeof jobStates)[number]>('all');
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [bulkStatus, setBulkStatus] = useState<EmployerJobStatus>('approved');

  const jobsQuery = useEmployerJobs({ status: activeTab });
  const updateMutation = useUpdateEmployerJobStatus({ status: activeTab });
  const bulkMutation = useBulkUpdateEmployerJobs({ status: activeTab });

  const jobs = jobsQuery.data ?? [];
  const filteredJobs = useMemo(
    () => (jobsQuery.data ?? []).filter((job) => activeTab === 'all' || job.status === activeTab),
    [jobsQuery.data, activeTab],
  );

  const toggleSelection = (id: string, checked: boolean | string) => {
    setSelectedJobs((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((jobId) => jobId !== id);
    });
  };

  const handleStatusChange = async (id: string, status: EmployerJobStatus) => {
    try {
      await updateMutation.mutateAsync({ id, status });
      toast.success('Job status updated. Audit log captured.');
    } catch (error) {
      console.error(error);
      toast.error('Unable to update job status right now.');
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedJobs.length === 0) return;
    try {
      await bulkMutation.mutateAsync({ ids: selectedJobs, status: bulkStatus });
      toast.success(`Updated ${selectedJobs.length} role(s). Audit log captured.`);
      setSelectedJobs([]);
    } catch (error) {
      console.error(error);
      toast.error('Bulk update failed.');
    }
  };

  return (
    <main className="min-h-screen bg-[#080a19] pb-24" aria-labelledby="job-management-title">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 id="job-management-title" className="text-3xl font-semibold text-white sm:text-4xl">Manage job postings</h1>
            <p className="text-sm text-gray-400">
              Track each role from draft to hire. Review moderation status and performance at a glance.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10">
              <Link to="/employer/job/new">Create new role</Link>
            </Button>
            <Button asChild className="bg-gold-gradient text-black hover:brightness-110">
              <Link to="/employer/job/wizard">AI Job Spec Builder</Link>
            </Button>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            setActiveTab(value as (typeof jobStates)[number]);
            setSelectedJobs([]);
          }}
          className="mt-10 space-y-6"
        >
          <TabsList className="flex flex-wrap gap-2 border border-white/10 bg-white/5 p-2">
            {jobStates.map((state) => (
              <TabsTrigger
                key={state}
                value={state}
                className="text-sm capitalize text-gray-200 data-[state=active]:bg-gold-gradient data-[state=active]:text-black"
              >
                {state.replace('_', ' ')}
              </TabsTrigger>
            ))}
          </TabsList>

          {selectedJobs.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-gray-200">
              <p>{selectedJobs.length} role(s) selected</p>
              <Select value={bulkStatus} onValueChange={(value: EmployerJobStatus) => setBulkStatus(value)}>
                <SelectTrigger className="w-48 border-white/20 bg-transparent text-white">
                  <SelectValue placeholder="Choose status" />
                </SelectTrigger>
                <SelectContent>
                  {jobStates
                    .filter((state) => state !== 'all')
                    .map((state) => (
                      <SelectItem key={state} value={state}>
                        {state.replace('_', ' ')}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleBulkUpdate}
                disabled={bulkMutation.isPending}
                className="bg-gold-gradient text-black hover:brightness-110"
              >
                {bulkMutation.isPending ? 'Updating…' : 'Update selected'}
              </Button>
              <Button variant="ghost" className="text-gray-400 hover:text-white" onClick={() => setSelectedJobs([])}>
                Clear selection
              </Button>
            </div>
          )}

          {jobStates.map((state) => (
            <TabsContent key={state} value={state} className="space-y-4">
              {jobsQuery.isPending ? (
                <Card className="border-white/10 bg-white/[0.02]">
                  <CardContent className="p-12 text-center text-sm text-gray-300">Loading jobs…</CardContent>
                </Card>
              ) : filteredJobs.length === 0 ? (
                <Card className="border-white/10 bg-white/[0.02]">
                  <CardContent className="p-12 text-center text-sm text-gray-300">
                    No roles in this state yet. Draft a new job or return to the overview.
                  </CardContent>
                </Card>
              ) : (
                filteredJobs.map((job) => {
                  const jobId = String(job.id);
                  const occupationLabel = job.occupationTitle ?? job.occupationId;
                  return (
                    <Card key={jobId} className="border-white/10 bg-white/[0.03]">
                      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedJobs.includes(jobId)}
                            onCheckedChange={(checked) => toggleSelection(jobId, Boolean(checked))}
                            aria-label={`Select ${job.title}`}
                          />
                          <div>
                            <CardTitle className="text-xl text-white">{job.title}</CardTitle>
                            <p className="text-sm text-gray-400">
                              {occupationLabel ? `Occupation: ${occupationLabel}` : 'Your organisation'}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <Badge className="border-white/10 bg-white/[0.08] text-gray-200">{job.type}</Badge>
                          <Select
                            value={job.status}
                            onValueChange={(value: EmployerJobStatus) => handleStatusChange(jobId, value)}
                          >
                            <SelectTrigger className="w-40 border-white/10 bg-white/[0.08] text-gray-100">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {jobStates
                                .filter((stateOption) => stateOption !== 'all')
                                .map((stateOption) => (
                                  <SelectItem key={stateOption} value={stateOption}>
                                    {stateOption.replace('_', ' ')}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4 p-6 pt-0">
                        <p className="text-sm text-gray-300 line-clamp-3">{job.description}</p>
                        <div className="grid gap-4 text-sm text-gray-300 md:grid-cols-3">
                          <div>
                            <p className="text-xs uppercase tracking-[0.25em] text-gray-500">Applicants</p>
                            <p className="mt-1 text-lg text-white">{job.applicants}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-[0.25em] text-gray-500">Views</p>
                            <p className="mt-1 text-lg text-white">{job.views}</p>
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-[0.25em] text-gray-500">Posted</p>
                            <p className="mt-1 text-lg text-white">{job.posted}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" asChild>
                            <Link to={`/employer/job/${jobId}/edit`} state={{ jobId }}>
                              Edit
                            </Link>
                          </Button>
                          <Button variant="outline" className="border-white/20 text-white hover:bg-white/10" asChild>
                            <Link to={`/employer/job/${jobId}/applicants`}>View applicants</Link>
                          </Button>
                          <Button
                            variant="ghost"
                            className="text-gray-300 hover:text-[#f7ef8a]"
                            onClick={() => toast.info('Audit log recorded for this transition.')}
                          >
                            View audit log
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </main>
  );
}
